// /home/dsingh/qchat/qchat_fe/src/config/featureFlags.js

/**
 * ============================================================
 * GLOBAL FEATURE FLAGS
 * ============================================================
 * 
 * Single source of truth for which features are enabled globally.
 * 
 * To toggle a feature:
 *   true  -> Feature is enabled
 *   false -> Feature is disabled
 * 
 * The sidebar automatically hides/shows tabs based on these flags.
 * 
 * IMPORTANT: When you enable a feature, you must also add the
 * corresponding tab mappings in `FEATURE_TAB_MAP` below.
 * 
 * ============================================================
 */

export const FEATURE_FLAGS = {
  // ============================================================
  // CORE FEATURES
  // ============================================================
  live_chat: false,
  qna_bot: false,
  leadgen: false,
  ai_agent: true,
};

/**
 * ============================================================
 * FEATURE → TAB MAPPING
 * ============================================================
 * 
 * Maps each feature flag to the list of sidebar group IDs
 * that should be shown when the feature is ENABLED.
 * 
 * When a feature is enabled (true), ONLY the groups listed
 * here will be visible for that feature.
 * 
 * When a feature is disabled (false), NONE of its groups
 * will be shown.
 * 
 * Group IDs must match the `id` values in
 * src/Component/SideData/Sidebar/itemList.js.
 * 
 * ============================================================
 */

export const FEATURE_TAB_MAP = {
  // ============================================================
  // AI AGENT (Currently the only active feature)
  // ============================================================
  ai_agent: [
    "credentials",           // Credentials tab
    "integrations_apis",     // Agent & Servers (API Config, ChainApis, Connections, Auth Integration)
    "ai_agent_workflows",    // ⭐ NEW — AI Agent Skill Workflow ONLY
    // "workflows",          // ❌ OLD — mixed Leadgen + AI Agent, removed
    "all_app_users",         // All Users (across apps)
    "share_developer",       // Share Developer
    "settings",              // Settings (General, Theme, Branding)
    "global_level_settings", // Global Level Settings (sub-items filtered by FEATURE_SUB_TAB_MAP)
  ],

  // ============================================================
  // LIVE CHAT (Currently disabled)
  // ============================================================
  live_chat: [
    // "live_agent",
    // "live_chat_history",
    // "live_agent_settings",
  ],

  // ============================================================
  // QNA BOT (Currently disabled)
  // ============================================================
  qna_bot: [
    // "leadgen_qna",
    // "qna_workflows",
    // "qna_templates",
  ],

  // ============================================================
  // LEADGEN (Currently disabled)
  // ============================================================
  leadgen: [
    // "leadgen_qna",
    // "leadgen_workflows",  // ⭐ Uncomment to enable Leadgen Workflow group
    // "lead_capture_settings",
  ],
};

/**
 * ============================================================
 * SUB-TAB FILTERS FOR GLOBAL LEVEL SETTINGS
 * ============================================================
 * 
 * Some parent groups (like Global Level Settings) have multiple
 * sub-items. This maps each feature to which sub-items should be shown.
 * 
 * ============================================================
 */

export const FEATURE_SUB_TAB_MAP = {
  ai_agent: {
    global_level_settings: [
      "global_api_configs",
      "global_chains",
      "global_auth",
      "global_connections",
      "whitelabel_domains",
    ],
  },
  live_chat: {},
  qna_bot: {},
  leadgen: {},
};

/**
 * ============================================================
 * HELPER FUNCTIONS
 * ============================================================
 */

/**
 * Check if a feature is enabled.
 * @param {string} featureName - The feature flag name
 * @returns {boolean}
 */
export const isFeatureEnabled = (featureName) => {
  return FEATURE_FLAGS[featureName] === true;
};

/**
 * Get all enabled feature names.
 * @returns {string[]}
 */
export const getEnabledFeatures = () => {
  return Object.entries(FEATURE_FLAGS)
    .filter(([_, enabled]) => enabled === true)
    .map(([name]) => name);
};

/**
 * Get all allowed tab IDs for currently enabled features.
 * @returns {string[]}
 */
export const getAllowedTabs = () => {
  const enabledFeatures = getEnabledFeatures();
  const allowedTabs = new Set();

  enabledFeatures.forEach((feature) => {
    const tabs = FEATURE_TAB_MAP[feature] || [];
    tabs.forEach((tab) => allowedTabs.add(tab));
  });

  return Array.from(allowedTabs);
};

/**
 * Check if a tab is allowed based on enabled features.
 * @param {string} tabId - The tab ID to check
 * @returns {boolean}
 */
export const isTabAllowed = (tabId) => {
  return getAllowedTabs().includes(tabId);
};

/**
 * Filter menu groups based on enabled features.
 * 
 * Called from itemList.js:
 *   export const menuGroups = filterMenuGroupsByFeatureFlags(baseMenuGroups);
 * 
 * @param {Array} menuGroups - The original menu groups array
 * @returns {Array} - Filtered menu groups
 */
export const filterMenuGroupsByFeatureFlags = (menuGroups) => {
  const allowedTabs = getAllowedTabs();
  const enabledFeatures = getEnabledFeatures();

  // If no features are enabled, return empty
  if (enabledFeatures.length === 0) {
    return [];
  }

  const filteredGroups = menuGroups
    .map((group) => {
      // Check if this group is allowed
      if (!allowedTabs.includes(group.id)) {
        return null;
      }

      // Filter items within the group
      const filteredItems = (group.items || []).filter((item) => {
        // For global_level_settings, check sub-tab map
        if (group.id === "global_level_settings") {
          const hasAnyEnabledSubTab = enabledFeatures.some((feature) => {
            const subTabs = FEATURE_SUB_TAB_MAP[feature]?.[group.id] || [];
            return subTabs.includes(item.id);
          });
          return hasAnyEnabledSubTab;
        }

        // For other groups, item is allowed if its group is allowed
        return true;
      });

      // If no items left, hide the group
      if (filteredItems.length === 0) {
        return null;
      }

      return {
        ...group,
        items: filteredItems,
      };
    })
    .filter(Boolean); // Remove nulls

  return filteredGroups;
};